import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Stage, Layer, Line, Rect, Circle, Text, Group, Arrow } from 'react-konva';
import Konva from 'konva';
import { useTranslation } from '@structura/i18n';

// Import types
interface SMBeamNode {
  id: number;
  supportType: 'Fixed' | 'Pinned' | 'Roller' | 'Free';
  axialFixed: boolean;
  position: number;
}

interface SMBeamLoad {
  id: string;
  type: 'UniformLoad' | 'PointLoad' | 'TriangularRight' | 'TriangularLeft' | 'AxialForce';
  spanId: number;
  category?: 'Permanent' | 'Variable' | 'Wind' | 'Snow' | 'Prestress';
  pointLoad: number;
  pointLoadPosition: number;
  normalForce: number;
  normalForcePosition: number;
  normalForceEccentricity: number;
  uniformIntensity: number;
  startIntensity: number;
  endIntensity: number;
  startPosition: number;
  endPosition: number;
  axialForceEnd?: 'left' | 'right';
}

interface SMBeamSpan {
  id: number;
  leftNodeId: number;
  rightNodeId: number;
  length: number;
  E: number;
  A: number;
  I: number;
  loads: SMBeamLoad[];
}

interface CrossSection {
  name: string;
  width: number;
  height: number;
  area: number;
  inertia: number;
  isCustom: boolean;
}

interface SMBeamModel {
  nodes: SMBeamNode[];
  spans: SMBeamSpan[];
  loadCases: any[];
  crossSection: CrossSection;
}

interface DiagramDataPoint {
  position: number;
  value: number;
}

interface DiagramData {
  success: boolean;
  data: DiagramDataPoint[];
  count: number;
  maxValue: number;
  unit: string;
}

interface BeamDiagramKonvaProps {
  model: SMBeamModel;
  diagramType: 'moment' | 'shear' | 'displacement' | 'axial' | 'axialDisp' | 'reactions' | 'none';
  diagramData?: DiagramData;
  reactionData?: any[];
  scale?: number;
  showValues?: boolean;
  showMaxMin?: boolean;
  valueDecimals?: number;
  valueDensity?: number;
  valueFontSize?: number;
  width?: number;
  height?: number;
  labelOverride?: string;
  colorOverride?: string;
  fillColorOverride?: string;
  scaleMultiplierOverride?: number;
  limitState?: string;
  leadingCategory?: string | null;
  provideCapture?: (capture: () => string) => void;
  thresholdValue?: number;
  thresholdFillColor?: string;
}

const BeamDiagramKonva: React.FC<BeamDiagramKonvaProps> = ({ 
  model, 
  diagramType,
  diagramData,
  reactionData,
  scale = 1,
  showValues = false,
  showMaxMin = true,
  valueDecimals = 2,
  valueDensity = 5,
  valueFontSize = 9,
  width: initialWidth = 1000,
  height: initialHeight = 500,
  labelOverride,
  colorOverride,
  fillColorOverride,
  scaleMultiplierOverride,
  limitState,
  leadingCategory,
  provideCapture,
  thresholdValue,
  thresholdFillColor
}) => {
  const { t } = useTranslation();
  const stageRef = useRef<Konva.Stage | null>(null);

  useEffect(() => {
    if (!provideCapture) return;
    const capture = () => {
      try {
        return stageRef.current?.toDataURL({ pixelRatio: 2, mimeType: 'image/png' }) || '';
      } catch {
        return '';
      }
    };
    provideCapture(capture);
  }, [provideCapture]);

  // Load saved dimensions from localStorage (lazy initialization)
  const [stageSize, setStageSize] = useState(() => {
    try {
      const saved = localStorage.getItem('beamDiagram_size');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { 
          width: Math.max(400, parsed.width || initialWidth), 
          height: Math.max(200, parsed.height || initialHeight) 
        };
      }
    } catch {
      // Silent fail - use defaults
    }
    return { width: initialWidth, height: initialHeight };
  });

  const [zoom, setZoom] = useState(() => {
    try {
      const saved = localStorage.getItem(`beamDiagram_view_${diagramType}`);
      if (saved) {
        const parsed = JSON.parse(saved) as { zoom?: number };
        if (typeof parsed.zoom === 'number' && isFinite(parsed.zoom)) {
          return Math.max(0.1, Math.min(5, parsed.zoom));
        }
      }
    } catch { /* localStorage unavailable */ }
    return 1;
  });
  const [stagePos, setStagePos] = useState(() => {
    try {
      const saved = localStorage.getItem(`beamDiagram_view_${diagramType}`);
      if (saved) {
        const parsed = JSON.parse(saved) as { x?: number; y?: number };
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          return { x: parsed.x, y: parsed.y };
        }
      }
    } catch { /* localStorage unavailable */ }
    return { x: 0, y: 0 };
  });
  const prevDiagramTypeRef = useRef(diagramType);
  const [isDragging, setIsDragging] = useState(false);

  const combinationLabel = useMemo(() => {
    const parts: string[] = [];
    if (limitState) {
      parts.push(limitState);
    }
    if (leadingCategory && leadingCategory !== 'None') {
      parts.push(`Leading: ${leadingCategory}`);
    }
    return parts.join(' • ');
  }, [limitState, leadingCategory]);

  // Save dimensions to localStorage whenever they change (shared for all diagrams)
  useEffect(() => {
    try {
      localStorage.setItem('beamDiagram_size', JSON.stringify(stageSize));
    } catch {
      // Silent fail
    }
  }, [stageSize]);

  // Load per-diagram view (zoom and pan) when diagram type changes (not on mount — initializers handle that)
  /* eslint-disable react-hooks/set-state-in-effect -- intentional restore of view state on diagram switch */
  useEffect(() => {
    if (prevDiagramTypeRef.current === diagramType) {
      return;
    }
    prevDiagramTypeRef.current = diagramType;
    try {
      const viewKey = `beamDiagram_view_${diagramType}`;
      const saved = localStorage.getItem(viewKey);
      if (saved) {
        const parsed = JSON.parse(saved) as { zoom?: number; x?: number; y?: number };
        if (typeof parsed.zoom === 'number' && isFinite(parsed.zoom)) {
          setZoom(Math.max(0.1, Math.min(5, parsed.zoom)));
        }
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          setStagePos({ x: parsed.x, y: parsed.y });
        }
      } else {
        setZoom(1);
        setStagePos({ x: 0, y: 0 });
      }
    } catch { /* localStorage unavailable */ }
  }, [diagramType]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Persist per-diagram view on changes
  useEffect(() => {
    try {
      const viewKey = `beamDiagram_view_${diagramType}`;
      localStorage.setItem(viewKey, JSON.stringify({ zoom, x: stagePos.x, y: stagePos.y }));
    } catch { /* localStorage unavailable */ }
  }, [diagramType, zoom, stagePos.x, stagePos.y]);

  // Resize handler
  const handleResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = stageSize.width;
    const startHeight = stageSize.height;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(400, startWidth + (e.clientX - startX));
      const newHeight = Math.max(200, startHeight + (e.clientY - startY));
      setStageSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [stageSize.width, stageSize.height]);

  // Calculate total beam length
  const totalLength = useMemo(() => 
    model.spans.reduce((sum, span) => sum + span.length, 0),
    [model.spans]
  );

  // Calculate scale for beam drawing
  const beamScale = useMemo(() => 
    (stageSize.width - 100) / totalLength,
    [stageSize.width, totalLength]
  );

  const beamY = stageSize.height / 2;
  const beamHeight = 20;
  const startX = 50;

  // Diagram configuration
  const diagramConfig = useMemo(() => {
    const configs = {
      moment: { color: '#007bff', fillColor: 'rgba(0, 123, 255, 0.2)', tensionSide: true, label: 'Moment (kNm)', scaleMultiplier: 1 },
      shear: { color: '#dc3545', fillColor: 'rgba(220, 53, 69, 0.2)', tensionSide: false, label: 'Shear (kN)', scaleMultiplier: 1 },
      displacement: { color: '#28a745', fillColor: 'rgba(40, 167, 69, 0.2)', tensionSide: false, label: 'Displacement (mm)', scaleMultiplier: 1 },
      axial: { color: '#ffc107', fillColor: 'rgba(255, 193, 7, 0.2)', tensionSide: false, label: 'Axial (kN)', scaleMultiplier: 1 },
      axialDisp: { color: '#6f42c1', fillColor: 'rgba(111, 66, 193, 0.2)', tensionSide: false, label: 'Axial Disp (mm)', scaleMultiplier: 1 },
      reactions: { color: '#17a2b8', fillColor: 'rgba(23, 162, 184, 0.2)', tensionSide: false, label: 'Reactions (kN)', scaleMultiplier: 1 },
      none: { color: '#6c757d', fillColor: 'rgba(108, 117, 125, 0.2)', tensionSide: false, label: '', scaleMultiplier: 1 }
    } as const;
    const base = (configs as any)[diagramType] || configs.none;
    return {
      color: colorOverride || base.color,
      fillColor: fillColorOverride || base.fillColor,
      tensionSide: base.tensionSide,
      label: labelOverride !== undefined ? labelOverride : base.label,
      scaleMultiplier: typeof scaleMultiplierOverride === 'number' ? scaleMultiplierOverride : base.scaleMultiplier
    };
  }, [diagramType, colorOverride, fillColorOverride, labelOverride, scaleMultiplierOverride]);

  // Calculate automatic diagram scale to fit in view
  const autoScale = useMemo(() => {
    if (!diagramData || diagramData.data.length === 0) return 1;
    
    const maxAbsValue = Math.max(...diagramData.data.map(d => Math.abs(d.value)));
    if (maxAbsValue === 0) return 1;
    
    // Base scale to fit nicely (max 100px offset from beam), then multiply by user's scale
    const baseScale = 100 / (maxAbsValue * diagramConfig.scaleMultiplier);
    return baseScale * scale; // Apply user's scale factor
  }, [diagramData, diagramConfig.scaleMultiplier, scale]);

  // Render support symbol
  const renderSupport = (node: SMBeamNode, x: number) => {
    const supportColors = {
      Fixed: '#dc3545',
      Pinned: '#28a745',
      Roller: '#ffc107',
      Free: '#6c757d'
    };

    const color = supportColors[node.supportType];

    switch (node.supportType) {
      case 'Fixed':
        return (
          <Group key={`support-${node.id}`}>
            <Rect
              x={x - 8}
              y={beamY + beamHeight / 2}
              width={16}
              height={25}
              fill={color}
            />
            {/* Hatching lines */}
            {[0, 1, 2, 3].map(i => (
              <Line
                key={`hatch-${i}`}
                points={[x - 8 + i * 4, beamY + beamHeight / 2 + 25, x - 8 + i * 4 + 3, beamY + beamHeight / 2 + 20]}
                stroke="white"
                strokeWidth={1}
              />
            ))}
            <Text
              x={x}
              y={beamY + beamHeight / 2 + 30}
              text={node.id.toString()}
              fontSize={12}
              fill="black"
              offsetX={6}
              align="center"
            />
          </Group>
        );

      case 'Pinned':
        return (
          <Group key={`support-${node.id}`}>
            <Line
              points={[x, beamY + beamHeight / 2, x - 12, beamY + beamHeight / 2 + 25, x + 12, beamY + beamHeight / 2 + 25]}
              closed
              fill={color}
            />
            <Line
              points={[x - 12, beamY + beamHeight / 2 + 25, x + 12, beamY + beamHeight / 2 + 25]}
              stroke="black"
              strokeWidth={2}
            />
            <Text
              x={x}
              y={beamY + beamHeight / 2 + 30}
              text={node.id.toString()}
              fontSize={12}
              fill="black"
              offsetX={6}
              align="center"
            />
          </Group>
        );

      case 'Roller':
        return (
          <Group key={`support-${node.id}`}>
            <Line
              points={[x, beamY + beamHeight / 2, x - 12, beamY + beamHeight / 2 + 20, x + 12, beamY + beamHeight / 2 + 20]}
              closed
              fill={color}
            />
            {/* Roller circles */}
            <Circle
              x={x - 6}
              y={beamY + beamHeight / 2 + 23}
              radius={3}
              fill="white"
              stroke={color}
              strokeWidth={1}
            />
            <Circle
              x={x + 6}
              y={beamY + beamHeight / 2 + 23}
              radius={3}
              fill="white"
              stroke={color}
              strokeWidth={1}
            />
            <Text
              x={x}
              y={beamY + beamHeight / 2 + 30}
              text={node.id.toString()}
              fontSize={12}
              fill="black"
              offsetX={6}
              align="center"
            />
          </Group>
        );

      default:
        return null;
    }
  };

  // Get eccentricity from axial force loads in the model
  const getAxialEccentricity = useMemo(() => {
    // Find any axial force load with eccentricity
    for (let i = 0; i < model.spans.length; i++) {
      const span = model.spans[i];
      for (let j = 0; j < span.loads.length; j++) {
        const load = span.loads[j];
        if (load.type === 'AxialForce' && Math.abs(load.normalForceEccentricity || 0) > 0.001) {
          return load.normalForceEccentricity;
        }
      }
    }
    return 0;
  }, [model.spans]);

  // Render reactions as arrows at supports
  const renderReactions = () => {
    if (!reactionData || reactionData.length === 0) return null;

    // Debug: Log reaction data to console
    console.log('Reaction data:', reactionData);

    const maxVerticalForce = Math.max(...reactionData.map((r: any) => Math.abs(r.rv || r.Rv || 0)), 1);
    const maxHorizontalForce = Math.max(...reactionData.map((r: any) => Math.abs(r.ru || r.Ru || 0)), 1);
    const maxMoment = Math.max(...reactionData.map((r: any) => Math.abs(r.m || r.M || 0)), 1);
    
    console.log('Max forces - Vertical:', maxVerticalForce, 'Horizontal:', maxHorizontalForce, 'Moment:', maxMoment);
    const verticalScale = 80 / maxVerticalForce;
    const horizontalScale = 80 / maxHorizontalForce;
    const momentScale = 30 / maxMoment;

    // Get eccentricity for horizontal force offset
    const eccentricity = getAxialEccentricity;
    const eccentricityScale = 100; // pixels per meter
    const eccentricityOffset = eccentricity * eccentricityScale;

    return reactionData.map((reaction: any, index: number) => {
      // Locate the support by its real node index — the backend skips free nodes, so the array
      // position is NOT the node index. Drawing by array index put arrows at the wrong supports.
      const nodeIndex = reaction.nodeIndex ?? reaction.NodeIndex ?? index;
      const node = model.nodes[nodeIndex];
      if (!node) return null;

      const nodeX = startX + model.spans.slice(0, nodeIndex).reduce((sum, s) => sum + s.length * beamScale, 0);
      
      // Vertical reaction force
      const rv = reaction.rv || reaction.Rv || 0;
      const verticalArrowLength = Math.abs(rv) * verticalScale;
      const isUpward = rv > 0;
      
      // Horizontal reaction force - offset by eccentricity
      const ru = reaction.ru || reaction.Ru || 0;
      const horizontalArrowLength = Math.abs(ru) * horizontalScale;
      const isRightward = ru > 0;
      const horizontalForceY = beamY - eccentricityOffset; // Same eccentricity as applied load
      
      // Reaction moment
      const m = reaction.m || reaction.M || 0;
      const momentRadius = Math.abs(m) * momentScale;
      const isClockwise = m < 0; // Negative moment is clockwise

      return (
        <Group key={`reaction-${nodeIndex}`}>
          {/* Vertical force arrow */}
          {Math.abs(rv) > 0.001 && (
            <>
              <Arrow
                points={[
                  nodeX, 
                  isUpward ? beamY + beamHeight / 2 + 60 : beamY - beamHeight / 2 - 60,
                  nodeX,
                  isUpward ? beamY + beamHeight / 2 + 60 - verticalArrowLength : beamY - beamHeight / 2 - 60 + verticalArrowLength
                ]}
                pointerLength={8}
                pointerWidth={8}
                fill={diagramConfig.color}
                stroke={diagramConfig.color}
                strokeWidth={3}
              />
              {showValues && (
                <Text
                  x={nodeX + 15}
                  y={isUpward ? beamY + beamHeight / 2 + 70 : beamY - beamHeight / 2 - 80}
                  text={`Rv=${rv.toFixed(2)} kN`}
                  fontSize={10}
                  fill={diagramConfig.color}
                  fontStyle="bold"
                  align="left"
                />
              )}
            </>
          )}
          
          {/* Horizontal force arrow - shown at eccentric position */}
          {Math.abs(ru) > 0.001 && (
            <>
              {/* Eccentricity offset line from centerline */}
              {Math.abs(eccentricity) > 0.001 && (
                <>
                  <Line
                    points={[nodeX, beamY, nodeX, horizontalForceY]}
                    stroke="#ffc107"
                    strokeWidth={1}
                    dash={[4, 4]}
                  />
                  <Circle
                    x={nodeX}
                    y={beamY}
                    radius={3}
                    fill="white"
                    stroke="#ffc107"
                    strokeWidth={1}
                  />
                </>
              )}
              <Arrow
                points={[
                  isRightward ? nodeX - 60 : nodeX + 60,
                  horizontalForceY,
                  isRightward ? nodeX - 60 + horizontalArrowLength : nodeX + 60 - horizontalArrowLength,
                  horizontalForceY
                ]}
                pointerLength={8}
                pointerWidth={8}
                fill="#ffc107"
                stroke="#ffc107"
                strokeWidth={3}
              />
              {showValues && (
                <Text
                  x={isRightward ? nodeX - 60 : nodeX + 60}
                  y={horizontalForceY - 15}
                  text={Math.abs(eccentricity) > 0.001 
                    ? `Ru=${ru.toFixed(2)} kN (e=${(eccentricity*1000).toFixed(0)}mm)`
                    : `Ru=${ru.toFixed(2)} kN`}
                  fontSize={10}
                  fill="#ffc107"
                  fontStyle="bold"
                  align="center"
                  offsetX={40}
                />
              )}
            </>
          )}
          
          {/* Moment symbol - curved arrow */}
          {Math.abs(m) > 0.001 && (
            <>
              <Circle
                x={nodeX}
                y={beamY - beamHeight / 2 - 40}
                radius={momentRadius + 10}
                stroke={diagramConfig.color}
                strokeWidth={2}
                dash={[5, 3]}
              />
              {/* Arrow head for moment direction */}
              <Arrow
                points={
                  isClockwise 
                    ? [nodeX + (momentRadius + 10), beamY - beamHeight / 2 - 40, 
                       nodeX + (momentRadius + 10) - 5, beamY - beamHeight / 2 - 40 - 8]
                    : [nodeX - (momentRadius + 10), beamY - beamHeight / 2 - 40,
                       nodeX - (momentRadius + 10) + 5, beamY - beamHeight / 2 - 40 - 8]
                }
                pointerLength={6}
                pointerWidth={6}
                fill={diagramConfig.color}
                stroke={diagramConfig.color}
                strokeWidth={2}
              />
              {showValues && (
                <Text
                  x={nodeX}
                  y={beamY - beamHeight / 2 - 40 - momentRadius - 20}
                  text={`M=${m.toFixed(2)} kNm`}
                  fontSize={10}
                  fill={diagramConfig.color}
                  fontStyle="bold"
                  offsetX={30}
                  align="center"
                />
              )}
            </>
          )}
        </Group>
      );
    });
  };

  // Render diagram curve
  const renderDiagram = () => {
    if (!diagramData || diagramData.data.length === 0) return null;

    const basePoints = [...diagramData.data];

    // Ensure diagram covers full beam length [0, totalLength] using a local copy,
    // without altering the original solver points used for response/UR.
    if (totalLength > 0 && basePoints.length > 0) {
      const tolerance = Math.max(totalLength * 1e-6, 1e-9);
      const firstPoint = basePoints[0];
      const lastPoint = basePoints[basePoints.length - 1];

      if (firstPoint.position > 0 + tolerance) {
        basePoints.unshift({
          position: 0,
          value: firstPoint.value
        });
      }

      if (lastPoint.position < totalLength - tolerance) {
        basePoints.push({
          position: totalLength,
          value: lastPoint.value
        });
      }
    }

    const points: number[] = [];
    const textMarkers: React.ReactElement[] = [];
    
    // Build curve points (same behavior for all internal forces, including axial)
    basePoints.forEach((point, i) => {
      const x = startX + (point.position / totalLength) * (totalLength * beamScale);
      const scaledValue = point.value * diagramConfig.scaleMultiplier * autoScale;
      
      // For moment diagrams, plot on tension side (below beam for positive moments)
      const y = diagramConfig.tensionSide 
        ? beamY + scaledValue  // Tension side: positive moments below beam (flip sign)
        : beamY - scaledValue; // Normal: positive values above
      
      points.push(x, y);

      // Add value labels at intervals based on valueDensity
      if (showValues && i % valueDensity === 0) {
        textMarkers.push(
          <Text
            key={`label-${i}`}
            x={x}
            y={y - 15}
            text={`${(point.value * diagramConfig.scaleMultiplier).toFixed(valueDecimals)}`}
            fontSize={valueFontSize}
            fill={diagramConfig.color}
            offsetX={15}
            align="center"
          />
        );
      }
    });

    // Create closed polygon for fill (add baseline points)
    const fillPoints = [...points];
    const lastX = points[points.length - 2];
    const firstX = points[0];
    fillPoints.push(lastX, beamY); // Last point to baseline
    fillPoints.push(firstX, beamY); // First point to baseline

    // Build threshold exceeded areas
    const thresholdElements: React.ReactElement[] = [];
    if (thresholdValue !== undefined && thresholdFillColor) {
      const thresholdScaled = thresholdValue * autoScale;
      const thresholdY = diagramConfig.tensionSide
        ? beamY + thresholdScaled
        : beamY - thresholdScaled;

      // Threshold reference line
      thresholdElements.push(
        <Line
          key="threshold-line"
          points={[startX, thresholdY, startX + totalLength * beamScale, thresholdY]}
          stroke="rgba(220,53,69,0.6)"
          strokeWidth={1.5}
          dash={[6, 4]}
        />
      );
      thresholdElements.push(
        <Text
          key="threshold-label"
          x={startX + totalLength * beamScale + 5}
          y={thresholdY - 6}
          text={`UR=${thresholdValue.toFixed(1)}`}
          fontSize={10}
          fill="rgba(220,53,69,0.8)"
          fontStyle="bold"
        />
      );

      // Build contiguous segments that exceed threshold
      type ThresholdPt = { x: number; y: number };
      const segments: ThresholdPt[][] = [];
      let currentSeg: ThresholdPt[] = [];

      for (let i = 0; i < basePoints.length; i++) {
        const pt = basePoints[i];
        const displayVal = pt.value * diagramConfig.scaleMultiplier;
        const screenX = startX + (pt.position / totalLength) * (totalLength * beamScale);
        const scaledVal = displayVal * autoScale;
        const screenY = diagramConfig.tensionSide ? beamY + scaledVal : beamY - scaledVal;
        const isAbove = displayVal > thresholdValue;

        if (isAbove) {
          // Interpolate entry point if starting a new segment
          if (currentSeg.length === 0 && i > 0) {
            const prevPt = basePoints[i - 1];
            const prevDisplay = prevPt.value * diagramConfig.scaleMultiplier;
            if (prevDisplay <= thresholdValue) {
              const t = (thresholdValue - prevDisplay) / (displayVal - prevDisplay);
              const prevX = startX + (prevPt.position / totalLength) * (totalLength * beamScale);
              const crossX = prevX + t * (screenX - prevX);
              currentSeg.push({ x: crossX, y: thresholdY });
            }
          }
          currentSeg.push({ x: screenX, y: screenY });
        } else {
          if (currentSeg.length > 0) {
            // Was above, now below — interpolate exit point
            const prevPt = basePoints[i - 1];
            const prevDisplay = prevPt.value * diagramConfig.scaleMultiplier;
            const t = (thresholdValue - prevDisplay) / (displayVal - prevDisplay);
            const prevX = startX + (prevPt.position / totalLength) * (totalLength * beamScale);
            const crossX = prevX + t * (screenX - prevX);
            currentSeg.push({ x: crossX, y: thresholdY });
            segments.push([...currentSeg]);
            currentSeg = [];
          }
        }
      }
      if (currentSeg.length > 0) {
        segments.push([...currentSeg]);
      }

      // Render each exceeded segment as a filled polygon
      segments.forEach((seg, si) => {
        const polyPts: number[] = [];
        seg.forEach(p => polyPts.push(p.x, p.y));
        // Close back along threshold line
        polyPts.push(seg[seg.length - 1].x, thresholdY);
        polyPts.push(seg[0].x, thresholdY);

        thresholdElements.push(
          <Line
            key={`threshold-fill-${si}`}
            points={polyPts}
            fill={thresholdFillColor}
            closed
            stroke="rgba(220,53,69,0.6)"
            strokeWidth={1}
          />
        );
      });
    }

    return (
      <Group key="diagram-group">
        {/* Filled area */}
        <Line
          points={fillPoints}
          fill={diagramConfig.fillColor}
          closed
        />
        {/* Threshold exceeded areas */}
        {thresholdElements}
        {/* Diagram curve */}
        <Line
          points={points}
          stroke={diagramConfig.color}
          strokeWidth={2}
          tension={0.4} // Smooth curve with tension
          lineCap="round"
          lineJoin="round"
        />
        {/* Value labels */}
        {textMarkers}
        
        {/* Max/Min markers - per span */}
        {showMaxMin && (() => {
          const markers: React.ReactElement[] = [];
          const addedMarkers: { position: number; value: number; type: string }[] = [];
          
          // Helper to check if a marker already exists at this position with same value
          const isDuplicate = (position: number, value: number, _type: string) => {
            const tolerance = 0.001;
            return addedMarkers.some(m => 
              Math.abs(m.position - position) < tolerance && 
              Math.abs(m.value - value) < tolerance
            );
          };
          
          // Get span boundaries from node positions
          const nodePositions = model.nodes.map(n => n.position).sort((a, b) => a - b);
          
          // Process each span
          for (let spanIndex = 0; spanIndex < nodePositions.length - 1; spanIndex++) {
            const spanStart = nodePositions[spanIndex];
            const spanEnd = nodePositions[spanIndex + 1];
            
            // Filter points within this span
            const spanPoints = diagramData.data.filter(p => 
              p.position >= spanStart - 0.001 && p.position <= spanEnd + 0.001
            );
            
            if (spanPoints.length === 0) continue;
            
            // Find max and min within this span
            const maxPoint = spanPoints.reduce((max, p) => p.value > max.value ? p : max, spanPoints[0]);
            const minPoint = spanPoints.reduce((min, p) => p.value < min.value ? p : min, spanPoints[0]);
            
            const maxValueScaled = maxPoint.value * diagramConfig.scaleMultiplier;
            const minValueScaled = minPoint.value * diagramConfig.scaleMultiplier;
            
            // Add max marker if significant and not duplicate
            if (Math.abs(maxValueScaled) > 0.01 && !isDuplicate(maxPoint.position, maxPoint.value, 'max')) {
              const maxX = startX + (maxPoint.position / totalLength) * (totalLength * beamScale);
              const maxScaledValue = maxPoint.value * diagramConfig.scaleMultiplier * autoScale;
              const maxY = diagramConfig.tensionSide ? beamY + maxScaledValue : beamY - maxScaledValue;
              
              markers.push(
                <Group key={`max-marker-span${spanIndex}`}>
                  <Circle x={maxX} y={maxY} radius={5} fill="green" stroke="white" strokeWidth={2} />
                  <Text
                    x={maxX}
                    y={maxY - 22}
                    text={`Max: ${maxValueScaled.toFixed(valueDecimals)}`}
                    fontSize={valueFontSize + 1}
                    fill="green"
                    fontStyle="bold"
                    offsetX={25}
                    align="center"
                  />
                </Group>
              );
              addedMarkers.push({ position: maxPoint.position, value: maxPoint.value, type: 'max' });
            }
            
            // Add min marker if significant, different from max, and not duplicate
            if (Math.abs(minValueScaled) > 0.01 && 
                Math.abs(minValueScaled - maxValueScaled) > 0.01 && 
                !isDuplicate(minPoint.position, minPoint.value, 'min')) {
              const minX = startX + (minPoint.position / totalLength) * (totalLength * beamScale);
              const minScaledValue = minPoint.value * diagramConfig.scaleMultiplier * autoScale;
              const minY = diagramConfig.tensionSide ? beamY + minScaledValue : beamY - minScaledValue;
              
              markers.push(
                <Group key={`min-marker-span${spanIndex}`}>
                  <Circle x={minX} y={minY} radius={5} fill="red" stroke="white" strokeWidth={2} />
                  <Text
                    x={minX}
                    y={minY + 18}
                    text={`Min: ${minValueScaled.toFixed(valueDecimals)}`}
                    fontSize={valueFontSize + 1}
                    fill="red"
                    fontStyle="bold"
                    offsetX={25}
                    align="center"
                  />
                </Group>
              );
              addedMarkers.push({ position: minPoint.position, value: minPoint.value, type: 'min' });
            }
          }
          
          return markers;
        })()}
      </Group>
    );
  };

  // Handle zoom
  const handleWheel = useCallback((e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    if (!stage) return;

    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale = e.evt.deltaY > 0 ? oldScale * 0.9 : oldScale * 1.1;
    const clampedScale = Math.max(0.1, Math.min(5, newScale));

    setZoom(clampedScale);

    const newPos = {
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    };

    setStagePos(newPos);
  }, []);

  // Reset view
  const resetView = useCallback(() => {
    setZoom(1);
    setStagePos({ x: 0, y: 0 });
  }, []);

  // Fit all
  const fitAll = useCallback(() => {
    const margin = 100;
    const availableWidth = stageSize.width - margin;
    const scaleX = availableWidth / totalLength;
    const optimalZoom = Math.min(scaleX, 2);
    
    setZoom(optimalZoom);
    setStagePos({ x: margin / 2, y: 0 });
  }, [stageSize.width, totalLength]);

  return (
    <div style={{ position: 'relative', width: 'fit-content', border: '1px solid #dee2e6', borderRadius: '0.375rem', overflow: 'visible' }}>
      {/* Control buttons */}
      <div style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        zIndex: 100,
        display: 'flex',
        gap: '4px'
      }}>
        <button
          onClick={fitAll}
          className="btn btn-sm btn-outline-primary"
          title={t('beam.viz.fitAll')}
          style={{ fontSize: '12px', padding: '2px 6px' }}
        >
          📐
        </button>
        <button
          onClick={resetView}
          className="btn btn-sm btn-outline-secondary"
          title={t('beam.viz.resetView')}
          style={{ fontSize: '12px', padding: '2px 6px' }}
        >
          🔄
        </button>
        <span className="badge bg-secondary" style={{ fontSize: '10px', padding: '2px 4px' }}>
          {Math.round(zoom * 100)}%
        </span>
      </div>

      <Stage
        width={stageSize.width}
        height={stageSize.height}
        scaleX={zoom}
        scaleY={zoom}
        x={stagePos.x}
        y={stagePos.y}
        onWheel={handleWheel}
        draggable
        onDragStart={() => setIsDragging(true)}
        onDragEnd={(e) => {
          setIsDragging(false);
          setStagePos({ x: e.target.x(), y: e.target.y() });
        }}
        style={{ 
          cursor: isDragging ? 'grabbing' : 'grab',
          backgroundColor: 'white'
        }}
        ref={(node) => { stageRef.current = node as unknown as Konva.Stage; }}
      >
        <Layer>
          {/* Draw beam spans */}
          {model.spans.map((span, index) => {
            const spanX = startX + model.spans.slice(0, index).reduce((sum, s) => sum + s.length * beamScale, 0);
            const spanWidth = span.length * beamScale;

            return (
              <Group key={`span-${span.id}`}>
                <Rect
                  x={spanX}
                  y={beamY - beamHeight / 2}
                  width={spanWidth}
                  height={beamHeight}
                  fill="#e9ecef"
                  stroke="#6c757d"
                  strokeWidth={2}
                />

                {/* Dimension line */}
                <Line
                  points={[spanX, beamY + beamHeight / 2 + 50, spanX + spanWidth, beamY + beamHeight / 2 + 50]}
                  stroke="#666"
                  strokeWidth={1}
                />
                <Text
                  x={spanX + spanWidth / 2}
                  y={beamY + beamHeight / 2 + 55}
                  text={`${span.length}m`}
                  fontSize={11}
                  fill="#666"
                  offsetX={15}
                  align="center"
                />
              </Group>
            );
          })}

          {/* Draw supports */}
          {model.nodes.map((node, index) => {
            const nodeX = startX + model.spans.slice(0, index).reduce((sum, s) => sum + s.length * beamScale, 0);
            return renderSupport(node, nodeX);
          })}

          {/* Draw diagram or reactions */}
          {diagramType === 'reactions' ? renderReactions() : renderDiagram()}
        </Layer>
      </Stage>

      {/* Resize handle */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '20px',
          height: '20px',
          background: 'linear-gradient(135deg, transparent 50%, #6c757d 50%)',
          cursor: 'se-resize',
          opacity: 0.7,
          zIndex: 200
        }}
        onMouseDown={handleResize}
        title={t('beam.viz.dragToResize')}
      />

      {/* Instructions */}
      <div style={{
        position: 'absolute',
        bottom: '4px',
        left: '8px',
        fontSize: '11px',
        color: '#6c757d',
        pointerEvents: 'none'
      }}>
        Scroll: Zoom • Drag: Pan • Corner: Resize • {diagramConfig.label}{combinationLabel ? ` • ${combinationLabel}` : ''}
      </div>
    </div>
  );
};

export default BeamDiagramKonva;

